// Weekly Site Report editor/viewer — the 13-section report. Site Managers
// edit their own draft (incl. once returned with comments); Client Admin /
// director / administrator review a submitted report (Reviewed / Accepted /
// Comments issued — return).
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, RefreshCw, Save, Send, Loader2, AlertTriangle, Sparkles, ChevronDown, Camera, Link2, Paperclip } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import WsrReviewReport from "./WsrReviewReport";
import { useOfflinePhotoQueue } from "@/hooks/useOfflinePhotoQueue";
import { OtherDevicePendingBanner } from "@/components/OtherDevicePendingBanner";
import { clearOtherDevicePending } from "@/lib/photoFlusher";
import {
    OVERVIEW_ITEMS, QUALITY_CHECKLIST_ITEMS, QUALITY_CHECKLIST_LABELS, HS_CHECKLIST_ITEMS,
    ENVIRONMENTAL_CHECKLIST_ITEMS, HS_AUTO_ITEMS, CDM_DUTY_ITEMS, INDUCTIONS_CHECKLIST_ITEMS,
    PLANT_OWNER_OPTIONS, PLANT_STATUS_OPTIONS, NARRATIVE_HINTS, SIGN_OFF_DECLARATION,
    fmtDate, fmtDateTime, fmtReportingWeek, inductionCountSummary, validateWsrSubmit, isBehindSchedule,
    getIn, setIn,
} from "./wsrConstants";
import {
    SectionCard, Field, TextInput, SelectInput, Pill, RagPillGroup, ChecklistGrid,
    TaggedTable, PhotoGrid, Hint, CommentField, Badge, CheckPillGroup, ClientActionsTable,
    PhotoSourcePickerDialog, LinkRecordDialog, RisksTable, ProgrammeWorksProgressTable, DelayImpactRecoverySection,
} from "./WsrKit";

function ReadOnlyProjectField({ value, projectId, testId }) {
    return (
        <div>
            <TextInput value={value} disabled onChange={() => {}} testId={testId} />
            <Link to={`/app/projects?edit=${projectId}`} data-testid={testId && `${testId}-edit-link`}
                  className="text-[10.5px] font-semibold mt-1 inline-block hover:underline" style={{ color: "#C75A1B" }}>
                Edit on project
            </Link>
        </div>
    );
}

const STATUS_META = {
    draft: { label: "Draft", bg: "#FDF0DC", fg: "#B5720A" },
    submitted: { label: "Submitted", bg: "#E4F1E6", fg: "#2E7D32" },
    reviewed: { label: "Reviewed", bg: "#EAF1FB", fg: "#2A5DB0" },
    accepted: { label: "Accepted", bg: "#E4F1E6", fg: "#2E7D32" },
    returned: { label: "Returned", bg: "#FBE3E1", fg: "#B3261E" },
};
const SENIOR_STATUS_LABEL = { reviewed: "Reviewed", accepted: "Accepted", returned: "Comments issued — returned to Site Manager" };
const CAN_EDIT_ROLES = ["site_manager", "director", "administrator", "consultant"];
const CAN_REVIEW_ROLES = ["client_admin", "director", "administrator"];

export default function WeeklyReportEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [report, setReport] = useState(null);
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [raisingItem, setRaisingItem] = useState(null);
    const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
    const [linkRecordOpen, setLinkRecordOpen] = useState(false);
    const [submitIssues, setSubmitIssues] = useState([]);
    const [recoveryActions, setRecoveryActions] = useState([]);
    const [recoveryCandidates, setRecoveryCandidates] = useState([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const autosaveTimer = useRef(null);
    const submitInFlight = useRef(false);

    useEffect(() => {
        api.get(`/weekly-reports/${id}`)
            .then((r) => { setReport(r.data); setContent(r.data.content); setDirty(false); })
            .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
        api.get(`/weekly-reports/${id}/recovery-actions`).then((r) => setRecoveryActions(r.data)).catch(() => {});
    }, [id]);

    const reloadReport = async () => {
        const { data } = await api.get(`/weekly-reports/${id}`);
        setReport(data); setContent(data.content);
    };

    // Shared offline photo queue (lib/photoFlusher.js) — same pipeline as
    // Audits & Inspections: compress -> IndexedDB write-ahead -> upload with backoff.
    const {
        pendingPhotos, queuePhoto: queuePhotoOnly, retry: retryQueuedPhoto,
        discard: discardQueuedPhoto, discardAll: discardAllPendingPhotos,
    } = useOfflinePhotoQueue({
        entityType: "weekly_report", entityId: id, enabled: !!report,
        recordLabel: report ? `WSR — ${report.project_name || "Project"} (w/c ${report.week_start || ""})` : undefined,
        onUploaded: reloadReport,
    });

    const refreshRecoveryActions = async () => {
        const { data } = await api.get(`/weekly-reports/${id}/recovery-actions`);
        setRecoveryActions(data);
    };
    const linkRecoveryAction = async (actionId) => {
        try {
            const { data } = await api.post(`/weekly-reports/${id}/recovery-actions/link`, { action_id: actionId });
            setRecoveryActions(data);
            toast.success("Action linked as a recovery action");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    const unlinkRecoveryAction = async (actionId) => {
        try {
            const { data } = await api.post(`/weekly-reports/${id}/recovery-actions/unlink`, { action_id: actionId });
            setRecoveryActions(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    const createRecoveryAction = async (form) => {
        try {
            const { data } = await api.post(`/weekly-reports/${id}/recovery-actions`, form);
            setRecoveryActions(data);
            toast.success("Recovery action created");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    const searchRecoveryCandidates = async (q) => {
        try {
            const { data } = await api.get(`/weekly-reports/${id}/recovery-actions/candidates`, { params: q ? { q } : {} });
            setRecoveryCandidates(data);
        } catch (e) { /* non-critical */ }
    };

    const canEdit = !!report && report.status === "draft" && CAN_EDIT_ROLES.includes(user?.role);

    const save = async (silent) => {
        if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
        setSaving(true);
        try {
            const { data } = await api.patch(`/weekly-reports/${id}`, { content });
            setReport(data); setContent(data.content); setDirty(false);
            if (!silent) toast.success("Draft saved");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    // Prefill Author / Position / Date once, the first time a Site Manager opens their own draft.
    useEffect(() => {
        if (!report || !content || !canEdit) return;
        const g2 = (p, fb) => getIn(content, p, fb);
        if (g2("sign_off.author_name") || g2("sign_off.position") || g2("sign_off.date")) return;
        let next = content;
        next = setIn(next, "sign_off.author_name", user?.name || "");
        next = setIn(next, "sign_off.position", user?.title || "");
        next = setIn(next, "sign_off.date", new Date().toISOString().slice(0, 10));
        setContent(next);
        setDirty(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report?.id, canEdit]);

    // Debounced autosave (~3s after the last edit).
    useEffect(() => {
        if (!canEdit || !dirty) return;
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        autosaveTimer.current = setTimeout(() => { save(true); }, 3000);
        return () => clearTimeout(autosaveTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, dirty, canEdit]);

    // Warn on browser close/refresh with unsaved changes.
    useEffect(() => {
        const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    if (!report || !content) return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading report…</div>;

    const set = (path, value) => { setContent((c) => setIn(c, path, value)); setDirty(true); };
    const g = (path, fb) => getIn(content, path, fb);
    const setHeader = (field, value) => {
        setContent((c) => setIn(setIn(c, `header.${field}`, value), `header.edited.${field}`, true));
        setDirty(true);
    };
    const setSignature = (val) => {
        setContent((c) => setIn(setIn(c, "sign_off.signature", val), "sign_off.signed_at", val ? new Date().toISOString() : null));
        setDirty(true);
    };
    const setExpiredCards = (field, value) => {
        setContent((c) => setIn(setIn(c, `inductions.competence.${field}`, value), "inductions.competence.expired_cards_edited", true));
        setDirty(true);
    };
    const setOverviewNote = (key, value) => {
        setContent((c) => setIn(setIn(c, `overview.${key}.note`, value), `overview.${key}.note_auto`, false));
        setDirty(true);
    };
    const dismissRisk = (row) => {
        setContent((c) => {
            let next = setIn(c, "risks.items", getIn(c, "risks.items", []).filter((r) => r.id !== row.id));
            if (row.source_ref) {
                const dismissed = getIn(next, "risks.dismissed", []);
                if (!dismissed.includes(row.source_ref)) next = setIn(next, "risks.dismissed", [...dismissed, row.source_ref]);
            }
            return next;
        });
        setDirty(true);
    };

    // Reviewer sign-off (Prompt 6) — works even once the report is no longer a
    // draft, so it's a dedicated endpoint rather than the normal content PATCH.
    const decideRisk = async (row, decision, comment) => {
        try {
            const { data } = await api.post(`/weekly-reports/${id}/risks/${row.id}/decide`, { decision, comment });
            setReport(data); setContent(data.content);
            toast.success(decision === "approve" ? "Risk closure approved" : "Risk sent back to Open");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const goBack = () => {
        if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
        navigate("/app/weekly-reports");
    };

    const refresh = async () => {
        setRefreshing(true);
        try {
            if (dirty) await save(true);
            const { data } = await api.post(`/weekly-reports/${id}/refresh`);
            setReport(data); setContent(data.content); setDirty(false);
            await refreshRecoveryActions();
            toast.success("Auto-pulled data refreshed from Resources, Inspections & Toolbox Talks");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setRefreshing(false); }
    };

    const addPlantRowToRegister = async (row) => {
        try {
            await api.post("/equipment/from-wsr-row", {
                client_id: report.client_id, project_id: report.project_id,
                item: row.item || "Untitled item", category: row.category || "Other",
                plant_no: row.plant_no, notes: row.action_taken,
            });
            toast.success("Added to the Equipment on Site register");
            await refresh();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const submit = async () => {
        if (submitInFlight.current) return;
        const hsUnanswered = HS_CHECKLIST_ITEMS.filter((i) => !g(`health_safety.checklist.${i}`, {}).value);
        const envUnanswered = ENVIRONMENTAL_CHECKLIST_ITEMS.filter((i) => !g(`health_safety.environmental_checklist.${i}`, {}).value);
        const cdmUnanswered = CDM_DUTY_ITEMS.filter((i) => !g(`cdm.duty_checklist.${i}`, {}).value);
        const inductionsUnanswered = INDUCTIONS_CHECKLIST_ITEMS.filter((i) => !g(`inductions.checklist.${i}`, {}).value);
        const totalUnanswered = hsUnanswered.length + envUnanswered.length + cdmUnanswered.length + inductionsUnanswered.length;
        const issues = validateWsrSubmit(content, recoveryActions.length);
        if (totalUnanswered) issues.unshift({ section: "7.0/8.0/9.0", message: `Answer every checklist item — Health & Safety, CDM, Inductions (${totalUnanswered} remaining)` });
        if (!g("sign_off.signature")) issues.unshift({ section: "13.0", message: "Please sign the report before submitting" });
        if (!g("sign_off.declaration_confirmed")) issues.unshift({ section: "13.0", message: "Please confirm the Site Manager declaration before submitting" });
        if (issues.length) {
            setSubmitIssues(issues);
            toast.error(`${issues.length} item(s) need attention before this report can be submitted`);
            const first = issues[0];
            document.getElementById(`wsr-section-${first.section.split("/")[0]}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
        }
        setSubmitIssues([]);
        submitInFlight.current = true;
        // Cancel any pending debounced autosave up front so it can never race with (and overwrite) this submit.
        if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
        setSubmitting(true);
        try {
            if (dirty) await save(true);
            const { data } = await api.post(`/weekly-reports/${id}/submit`);
            setReport(data); setContent(data.content); setDirty(false);
            toast.success("Weekly Site Report submitted");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && Array.isArray(detail.issues)) { setSubmitIssues(detail.issues); toast.error(detail.message || "This report is missing required information"); }
            else toast.error(formatApiError(detail));
        }
        finally { setSubmitting(false); submitInFlight.current = false; }
    };

    const uploadPhoto = async (fileList) => {
        const files = Array.from(fileList);
        let queuedCount = 0;
        for (const file of files) {
            try { await queuePhotoOnly(file, {}); queuedCount += 1; }
            catch (e) { toast.error(`Could not save ${file.name} on this device`); }
        }
        if (!queuedCount) return;
        if (navigator.onLine) toast.success(queuedCount > 1 ? `${queuedCount} photos added — uploading…` : "Photo added — uploading…");
        else toast.info(queuedCount > 1 ? `${queuedCount} photos saved on this device — will upload automatically` : "Photo saved on this device — will upload automatically");
    };
    const deletePhoto = async (photoId) => {
        try {
            await api.delete(`/weekly-reports/${id}/photos/${photoId}`);
            setContent((c) => setIn(c, "evidence.photos", getIn(c, "evidence.photos", []).filter((p) => p.id !== photoId)));
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    // Prompt 12, item A — caption is persisted immediately via its own
    // endpoint so it survives leaving the page without a full Save draft.
    const captionPhoto = (photoId, caption, commit) => {
        setContent((c) => setIn(c, "evidence.photos", getIn(c, "evidence.photos", []).map((p) => (p.id === photoId ? { ...p, caption } : p))));
        setDirty(true);
        if (!commit) return;
        api.patch(`/weekly-reports/${id}/photos/${photoId}`, { caption }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
    };
    const addLinkedPhoto = (photo) => {
        setContent((c) => setIn(c, "evidence.photos", [...(getIn(c, "evidence.photos", [])), photo]));
        setDirty(true);
    };
    const addLinkedAttachment = (row) => {
        setContent((c) => setIn(c, "evidence.attachments", [...(getIn(c, "evidence.attachments", [])), row]));
        setDirty(true);
    };
    const uploadAttachment = async (file) => {
        if (!file) return;
        setUploadingAttachment(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", file.name);
            const { data } = await api.post(`/weekly-reports/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setContent((c) => setIn(c, "evidence.attachments", [...(getIn(c, "evidence.attachments", [])), data]));
            toast.success("Document uploaded");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed"); }
        finally { setUploadingAttachment(false); }
    };

    // "Mark remaining as Yes" — only touches items with no answer yet (item D),
    // scoped to whichever checklist(s) the caller passes.
    const markRemainingYes = (targets) => {
        setContent((c) => {
            let next = c;
            for (const [path, items] of targets) {
                const current = getIn(next, path, {});
                const patched = { ...current };
                for (const item of items) {
                    if (!(patched[item] || {}).value) {
                        patched[item] = { ...(patched[item] || {}), value: "Yes", edited_by_user: true };
                    }
                }
                next = setIn(next, path, patched);
            }
            return next;
        });
        setDirty(true);
    };

    // "Raise action" next to a "No" answer — saves first so the server has the
    // latest note, then persists the new action id straight into content.
    const raiseAction = async (section, checklistKey, item) => {
        if (dirty) await save(true);
        setRaisingItem(item);
        try {
            const { data } = await api.post(`/weekly-reports/${id}/raise-action`, { section, checklist: checklistKey, item });
            setContent(data.content);
            toast.success(`Action ${data.action.ref} raised`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setRaisingItem(null); }
    };

    const downloadPdf = async () => {
        try {
            const res = await api.get(`/weekly-reports/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `${report.report_number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Could not generate the PDF"); }
    };
    const downloadDocx = async () => {
        try {
            const res = await api.get(`/weekly-reports/${id}/docx`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `${report.report_number}.docx`; document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Could not generate the Word document"); }
    };

    const lastRevision = (report.revision_history || []).slice(-1)[0];
    const isReturned = report.status === "draft" && lastRevision?.status === "returned";
    const statusKey = isReturned ? "returned" : report.status;
    const statusMeta = STATUS_META[statusKey] || STATUS_META.draft;
    const senior = g("sign_off.senior_review", {});
    const canReview = CAN_REVIEW_ROLES.includes(user?.role);
    const sectionComments = senior.section_comments || {};
    const reviewingSubmitted = canReview && report.status === "submitted";

    return (
        <div data-testid="weekly-report-editor" className="space-y-6 max-w-5xl">
            <div className="flex items-center justify-between gap-3 flex-wrap sticky top-0 z-10 bg-[#F5F3F0] py-2 -mt-2">
                <button data-testid="wsr-back" onClick={goBack}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F]">
                    <ArrowLeft size={15} /> Weekly Site Reports
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                    <span data-testid="wsr-status-pill" className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: statusMeta.bg, color: statusMeta.fg }}>{statusMeta.label}</span>
                    {report.last_refreshed_at && (
                        <span className="text-[11px] text-[#9A968F]">Last refreshed {fmtDateTime(report.last_refreshed_at)}</span>
                    )}
                    {canEdit && (
                        <button data-testid="wsr-refresh" onClick={refresh} disabled={refreshing}
                                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0] disabled:opacity-50">
                            {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh data
                        </button>
                    )}
                    <ExportMenu onPdf={downloadPdf} onDocx={downloadDocx} />
                    {canEdit && (
                        <button data-testid="wsr-save" onClick={() => save(false)} disabled={saving || !dirty}
                                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0] disabled:opacity-50">
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save draft
                        </button>
                    )}
                    {canEdit && (
                        <button data-testid="wsr-submit" onClick={submit} disabled={submitting}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212] disabled:opacity-50">
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Submit report
                        </button>
                    )}
                </div>
            </div>

            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid #C75A1B", color: "#6F6A64" }}>
                    {report.report_number} · {report.client_name}
                </span>
                <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#1F1F1F]">{report.project_name}</h2>
                <p className="text-[13px] mt-1 text-[#6F6A64]">Reporting week: {fmtDate(report.week_start)} – {fmtDate(report.week_end)}</p>
            </div>

            {isReturned && (
                <div data-testid="wsr-returned-banner" className="rounded-xl border p-3.5 flex gap-2.5" style={{ background: "#FBE3E1", borderColor: "#F3C6C2" }}>
                    <AlertTriangle size={18} className="text-[#B3261E] shrink-0 mt-0.5" />
                    <div>
                        <div className="text-[13px] font-bold text-[#B3261E]">Comments issued — please review and resubmit</div>
                        <p className="text-[12.5px] text-[#8A2A21] mt-1">{lastRevision?.note || senior.comments || "No comments provided."}</p>
                        <p className="text-[11px] text-[#8A2A21] mt-1">By {lastRevision?.by_name || senior.reviewer_name} on {fmtDateTime(lastRevision?.at || senior.at)}</p>
                    </div>
                </div>
            )}

            {submitIssues.length > 0 && (
                <div data-testid="wsr-submit-issues-banner" className="rounded-xl border p-3.5" style={{ background: "#FBF3EC", borderColor: "#F2DAAD" }}>
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={16} className="text-[#C75A1B] shrink-0" />
                        <div className="text-[13px] font-bold text-[#9F4212]">{submitIssues.length} item(s) need attention before this report can be submitted</div>
                    </div>
                    <ul className="space-y-1 pl-1">
                        {submitIssues.map((iss, i) => (
                            <li key={i}>
                                <button type="button" data-testid={`wsr-submit-issue-${i}`}
                                        onClick={() => document.getElementById(`wsr-section-${iss.section.split("/")[0]}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                                        className="text-[12px] text-[#9F4212] hover:underline text-left">
                                    <span className="font-bold">{iss.section}</span> — {iss.message}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {reviewingSubmitted ? (
                <WsrReviewReport report={report} content={content} reportId={id} role={user?.role}
                                 defaultTitle={user?.title || ""} onDecideRisk={decideRisk}
                                 onDone={(data) => { setReport(data); setContent(data.content); }} />
            ) : (
            <>
            <SectionCard n="" title="Report details">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Project / Contract">
                        <ReadOnlyProjectField value={g("header.project_name") || report.project_name} projectId={report.project_id} testId="wsr-header-project-name" />
                    </Field>
                    <Field label="Project code">
                        <ReadOnlyProjectField value={g("header.project_code")} projectId={report.project_id} testId="wsr-header-project-code" />
                    </Field>
                    <Field label="Contract reference">
                        <ReadOnlyProjectField value={g("header.contract_ref")} projectId={report.project_id} testId="wsr-header-contract" />
                    </Field>
                    <Field label="Client / Employer">
                        <ReadOnlyProjectField value={g("header.client_employer") || report.client_name} projectId={report.project_id} testId="wsr-header-client-employer" />
                    </Field>
                    <Field label="Client representative">
                        <ReadOnlyProjectField value={g("header.client_rep")} projectId={report.project_id} testId="wsr-header-rep" />
                    </Field>
                    <Field label="Report number"><TextInput value={report.report_number} disabled onChange={() => {}} testId="wsr-header-report-number" /></Field>
                    <Field label="Reporting week"><TextInput value={fmtReportingWeek(report.week_start, report.week_end)} disabled onChange={() => {}} testId="wsr-header-week" /></Field>
                    <Field label="Site Manager"><TextInput value={g("header.site_manager_name")} disabled={!canEdit} onChange={(v) => setHeader("site_manager_name", v)} testId="wsr-header-manager" /></Field>
                    <Field label="Prepared by"><TextInput value={g("header.prepared_by")} disabled={!canEdit} onChange={(v) => setHeader("prepared_by", v)} testId="wsr-header-prepared-by" /></Field>
                    <Field label="Distribution"><TextInput value={g("header.distribution")} disabled={!canEdit} onChange={(v) => setHeader("distribution", v)} placeholder="Names / roles this report is circulated to" testId="wsr-header-distribution" /></Field>
                </div>
            </SectionCard>

            {/* 1.0 Overview */}
            <SectionCard n="1.0" title="Weekly Report Overview" reviewNote={isReturned ? sectionComments.overview : null}>
                <div className="grid sm:grid-cols-2 gap-3">
                    {OVERVIEW_ITEMS.map((it) => {
                        const suggested = g(`overview.${it.key}.suggested`);
                        const selected = g(`overview.${it.key}.selected`);
                        return (
                        <div key={it.key} className="rounded-xl border border-[#E5E0DA] p-3 space-y-2">
                            <div className="flex items-start justify-between gap-1.5 flex-wrap">
                                <div>
                                    <div className="text-[13px] font-bold text-[#1F1F1F]">{it.label}</div>
                                    <div className="text-[11px] text-[#8B867E]">{it.sub}</div>
                                </div>
                                {suggested && !selected && (
                                    <Badge tone="suggested"><Sparkles size={9} className="inline -mt-0.5 mr-0.5" />Suggested</Badge>
                                )}
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                                {it.options.map((o) => (
                                    <Pill key={o} active={selected === o} suggested={!selected && suggested === o} disabled={!canEdit}
                                          onClick={() => set(`overview.${it.key}.selected`, o)}
                                          testId={`wsr-overview-${it.key}-${o.toLowerCase().replace(/[^a-z]/g, "")}`}>{it.optionLabels?.[o] || o}</Pill>
                                ))}
                            </div>
                            <TextInput value={g(`overview.${it.key}.note`)} disabled={!canEdit} placeholder="Note"
                                       onChange={(v) => setOverviewNote(it.key, v)} testId={`wsr-overview-${it.key}-note`} />
                        </div>
                        );
                    })}
                </div>
            </SectionCard>

            {/* 2.0 Executive Summary */}
            <SectionCard n="2.0" title="Executive Summary" reviewNote={isReturned ? sectionComments.exec_summary : null}>
                <Hint text={NARRATIVE_HINTS.exec_summary} />
                <Field label="Key achievements this week"><TextInput textarea value={g("exec_summary.key_achievements")} disabled={!canEdit} onChange={(v) => set("exec_summary.key_achievements", v)} testId="wsr-exec-achievements" /></Field>
                <Field label="Key concerns / escalations"><TextInput textarea value={g("exec_summary.key_concerns")} disabled={!canEdit} onChange={(v) => set("exec_summary.key_concerns", v)} testId="wsr-exec-concerns" /></Field>
                <Field label="Outlook - what must happen next week to stay on track"><TextInput textarea value={g("exec_summary.outlook")} disabled={!canEdit} onChange={(v) => set("exec_summary.outlook", v)} testId="wsr-exec-outlook" /></Field>
            </SectionCard>

            {/* 3.0 Programme — Works Progress (fixed Planned vs Actual row) leads, then Delay/Impact/Recovery (required only when behind schedule) */}
            <SectionCard n="3.0" title="Programme Update & Status" reviewNote={isReturned ? sectionComments.programme : null}>
                {(() => {
                    const pva = g("programme.planned_vs_actual", {});
                    const behind = isBehindSchedule(pva);
                    return (
                        <>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Works progress</div>
                                <a href={`/app/projects/${report.project_id}#works-${report.project_id}`} target="_blank" rel="noreferrer"
                                   data-testid="wsr-works-progress-update-link" className="text-[11px] font-semibold text-[#C75A1B] hover:underline">
                                    Update % complete &rarr;
                                </a>
                            </div>
                            <ProgrammeWorksProgressTable testId="wsr-works-progress" disabled={!canEdit}
                                pva={pva} onPvaChange={(v) => set("programme.planned_vs_actual", v)}
                                rows={g("programme.works_progress", [])} onRowsChange={(v) => set("programme.works_progress", v)} />
                            {pva.legacy_note && (
                                <div data-testid="wsr-planned-actual-legacy-note" className="text-[12.5px] text-[#6F6A64] italic px-1">
                                    <span className="font-semibold not-italic">Previous note:</span> {pva.legacy_note}
                                </div>
                            )}
                            <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] pt-2">Delay, impact &amp; recovery</div>
                            <DelayImpactRecoverySection testId="wsr-delay-impact-recovery" disabled={!canEdit} behind={behind}
                                delay={g("programme.delay", {})} onDelayChange={(v) => set("programme.delay", v)}
                                impact={g("programme.programme_impact", {})} onImpactChange={(v) => set("programme.programme_impact", v)}
                                recoveryActions={recoveryActions} onLinkAction={linkRecoveryAction} onUnlinkAction={unlinkRecoveryAction}
                                onCreateAction={createRecoveryAction} candidates={recoveryCandidates} onSearchCandidates={searchRecoveryCandidates} />
                        </>
                    );
                })()}
                <div className="space-y-2 pt-2">
                    {[["time", "Time"], ["cost", "Cost"], ["information", "Information / Procurement"]].map(([k, lbl]) => {
                        const ragEntry = g(`programme.rag.${k}`, {});
                        const setRag = (patch) => set(`programme.rag.${k}`, { ...ragEntry, ...patch, edited_by_user: true });
                        return (
                        <div key={k} className="flex items-center gap-3 flex-wrap rounded-xl border border-[#E5E0DA] p-3">
                            <div className="w-40 text-[13px] font-semibold text-[#1F1F1F] flex items-center gap-1.5 flex-wrap">
                                {lbl}
                                {ragEntry.auto_suggested && !ragEntry.edited_by_user && (
                                    <Badge tone="suggested"><Sparkles size={9} className="inline -mt-0.5 mr-0.5" />Suggested</Badge>
                                )}
                            </div>
                            <RagPillGroup value={ragEntry.rag} disabled={!canEdit} onChange={(v) => setRag({ rag: v })} testId={`wsr-programme-rag-${k}`} />
                            <div className="flex-1 min-w-[180px]"><TextInput value={ragEntry.note} disabled={!canEdit} placeholder="Commentary / mitigation" onChange={(v) => setRag({ note: v })} testId={`wsr-programme-rag-${k}-note`} /></div>
                        </div>
                        );
                    })}
                </div>
            </SectionCard>

            {/* 4.0 Resources */}
            <SectionCard n="4.0" title="Resources On Site" reviewNote={isReturned ? sectionComments.resources : null}>
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Resource register — auto, from sign-on / attendance</div>
                <TaggedTable testId="wsr-resource-register" addLabel="Add resource category" refBadge="Attendance"
                          columns={[{ key: "category", label: "Resource category", width: "26%" }, { key: "count", label: "Avg this week", width: "16%" }, { key: "vs_last_week", label: "vs last week", width: "16%" }, { key: "comments", label: "Comments", width: "42%", textarea: true }]}
                          rows={g("resources.register", [])} disabled={!canEdit} onChange={(v) => set("resources.register", v)} />
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] pt-1">Subcontractor performance review — auto, from contractors, project subcontractors &amp; sign-in</div>
                <TaggedTable testId="wsr-subcontractor-reviews" addLabel="Add subcontractor" refBadge="Subcontractors"
                          columns={[{ key: "subcontractor", label: "Subcontractor", width: "16%" }, { key: "trade", label: "Trade", width: "12%" }, { key: "days_on_site", label: "Days on site", width: "18%", readOnly: true }, { key: "attendance", label: "Attend.", width: "9%" }, { key: "quality", label: "Quality", width: "9%" }, { key: "hs", label: "H&S", width: "9%" }, { key: "issues", label: "Issues / action", width: "27%", textarea: true }]}
                          rows={g("resources.subcontractor_reviews", [])} disabled={!canEdit} onChange={(v) => set("resources.subcontractor_reviews", v)} />
                <CommentField value={g("resources.narrative")} disabled={!canEdit} onChange={(v) => set("resources.narrative", v)} testId="wsr-resources-narrative" />
            </SectionCard>

            {/* 5.0 Plant */}
            <SectionCard n="5.0" title="Plant / Equipment On Site" reviewNote={isReturned ? sectionComments.plant : null}>
                <TaggedTable testId="wsr-plant-items" addLabel="Add plant / equipment" refBadge="Equipment on Site register"
                          columns={[
                              { key: "item", label: "Plant / equipment", width: "15%", autoReadOnly: true },
                              { key: "category", label: "Category", width: "9%", autoReadOnly: true },
                              { key: "plant_no", label: "Plant no.", width: "9%", autoReadOnly: true },
                              { key: "owner_hire", label: "Owner / hire", width: "10%", type: "select", options: PLANT_OWNER_OPTIONS, autoReadOnly: true },
                              { key: "status", label: "Status", width: "9%", type: "select", options: PLANT_STATUS_OPTIONS, autoReadOnly: true },
                              { key: "loler_status", label: "LOLER", width: "8%", autoReadOnly: true },
                              { key: "puwer_status", label: "PUWER", width: "8%", autoReadOnly: true },
                              { key: "action_taken", label: "Action taken", width: "14%", textarea: true, autoReadOnly: true },
                              { key: "comments", label: "Comments", width: "18%", textarea: true },
                          ]}
                          rowAction={report.equipment_register_enabled ? {
                              label: "Add to register",
                              show: (row) => row.source === "manual" && !row.equipment_item_id && !!(row.item || "").trim(),
                              onClick: addPlantRowToRegister,
                          } : undefined}
                          rows={g("plant.items", [])} disabled={!canEdit} onChange={(v) => set("plant.items", v)} />
                <CommentField value={g("plant.narrative")} disabled={!canEdit} onChange={(v) => set("plant.narrative", v)} testId="wsr-plant-narrative" />
                <label className="flex items-center gap-2 text-[12.5px] font-semibold text-[#1F1F1F] pt-1">
                    <input type="checkbox" data-testid="wsr-plant-no-plant" disabled={!canEdit} checked={!!g("plant.no_plant_on_site")}
                           onChange={(e) => set("plant.no_plant_on_site", e.target.checked)} />
                    No plant or equipment on site this week
                </label>
            </SectionCard>

            {/* 6.0 Quality */}
            <SectionCard n="6.0" title="Quality Inspections" reviewNote={isReturned ? sectionComments.quality : null}>
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Inspection log — auto, from Audits & Inspections</div>
                <TaggedTable testId="wsr-quality-log" addLabel="Add inspection" refBadge="Inspections"
                          columns={[{ key: "date", label: "Date", width: "12%" }, { key: "inspection", label: "Inspection", width: "28%" }, { key: "itp_stage", label: "ITP (Inspection & Test Plan) stage", width: "16%" }, { key: "owner", label: "Owner", width: "14%" }, { key: "result", label: "Result / follow-up", width: "30%", textarea: true }]}
                          rows={g("quality.log", [])} disabled={!canEdit} onChange={(v) => set("quality.log", v)} />
                <ChecklistGrid items={QUALITY_CHECKLIST_ITEMS} labels={QUALITY_CHECKLIST_LABELS} value={g("quality.checklist", {})} disabled={!canEdit} onChange={(v) => set("quality.checklist", v)}
                               testId="wsr-quality-checklist" showNeedsAnswer onRaiseAction={(item) => raiseAction("quality", "checklist", item)} raisingItem={raisingItem} />
                <CommentField value={g("quality.narrative")} disabled={!canEdit} onChange={(v) => set("quality.narrative", v)} testId="wsr-quality-narrative" />
            </SectionCard>

            {/* 7.0 Health & Safety */}
            <SectionCard n="7.0" title="Health & Safety Inspections" reviewNote={isReturned ? sectionComments.health_safety : null} action={(() => {
                const hsAnswered = HS_CHECKLIST_ITEMS.filter((i) => g(`health_safety.checklist.${i}`, {}).value).length;
                const envAnswered = ENVIRONMENTAL_CHECKLIST_ITEMS.filter((i) => g(`health_safety.environmental_checklist.${i}`, {}).value).length;
                const total = HS_CHECKLIST_ITEMS.length + ENVIRONMENTAL_CHECKLIST_ITEMS.length;
                const remaining = total - (hsAnswered + envAnswered);
                return (
                    <span data-testid="wsr-hs-checklist-counter" className="text-[11.5px] font-semibold text-[#8B867E]">
                        {HS_AUTO_ITEMS.length} auto-filled &middot; {total - HS_AUTO_ITEMS.length} to answer &middot; <span style={{ color: remaining ? "#B3261E" : "#2E7D32" }}>{remaining} remaining</span>
                    </span>
                );
            })()}>
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Inspection log — auto, from Audits & Inspections</div>
                <TaggedTable testId="wsr-hs-log" addLabel="Add inspection / event" refBadge="Inspections"
                          columns={[{ key: "date", label: "Date", width: "14%" }, { key: "inspection", label: "Inspection / event", width: "40%" }, { key: "owner", label: "Owner", width: "16%" }, { key: "result", label: "Status / action", width: "30%", textarea: true }]}
                          rows={g("health_safety.log", [])} disabled={!canEdit} onChange={(v) => set("health_safety.log", v)} />
                <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
                    <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Safety compliance check</div>
                    {canEdit && (
                        <button type="button" data-testid="wsr-hs-mark-remaining-yes"
                                onClick={() => markRemainingYes([["health_safety.checklist", HS_CHECKLIST_ITEMS], ["health_safety.environmental_checklist", ENVIRONMENTAL_CHECKLIST_ITEMS]])}
                                className="text-[11.5px] font-semibold text-[#C75A1B] hover:underline">
                            Mark remaining as Yes
                        </button>
                    )}
                </div>
                <ChecklistGrid items={HS_CHECKLIST_ITEMS} value={g("health_safety.checklist", {})} disabled={!canEdit}
                               onChange={(v) => set("health_safety.checklist", v)} testId="wsr-hs-checklist"
                               showNeedsAnswer onRaiseAction={(item) => raiseAction("health_safety", "checklist", item)} raisingItem={raisingItem} />
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] pt-1">Environmental check</div>
                <ChecklistGrid items={ENVIRONMENTAL_CHECKLIST_ITEMS} value={g("health_safety.environmental_checklist", {})} disabled={!canEdit}
                               onChange={(v) => set("health_safety.environmental_checklist", v)} testId="wsr-env-checklist"
                               showNeedsAnswer onRaiseAction={(item) => raiseAction("health_safety", "environmental_checklist", item)} raisingItem={raisingItem} />
                <CommentField value={g("health_safety.narrative")} disabled={!canEdit} onChange={(v) => set("health_safety.narrative", v)} testId="wsr-hs-narrative" />
            </SectionCard>

            {/* 8.0 CDM */}
            <SectionCard n="8.0" title="CDM Compliance Checklist" reviewNote={isReturned ? sectionComments.cdm : null}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">CDM duty checklist</div>
                    {canEdit && (
                        <button type="button" data-testid="wsr-cdm-mark-remaining-yes"
                                onClick={() => markRemainingYes([["cdm.duty_checklist", CDM_DUTY_ITEMS]])}
                                className="text-[11.5px] font-semibold text-[#C75A1B] hover:underline">
                            Mark remaining as Yes
                        </button>
                    )}
                </div>
                <ChecklistGrid items={CDM_DUTY_ITEMS} value={g("cdm.duty_checklist", {})} disabled={!canEdit} onChange={(v) => set("cdm.duty_checklist", v)}
                               testId="wsr-cdm-checklist" showNeedsAnswer onRaiseAction={(item) => raiseAction("cdm", "duty_checklist", item)} raisingItem={raisingItem} />
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] pt-1">Site control</div>
                <TaggedTable testId="wsr-cdm-site-control" addLabel="Add site control area"
                          columns={[{ key: "area", label: "Site control area", width: "30%" }, { key: "position", label: "Current position", width: "30%", type: "select", optionsFromRow: true }, { key: "action_owner", label: "Action / owner", width: "40%", textarea: true }]}
                          rows={g("cdm.site_control", [])} disabled={!canEdit} onChange={(v) => set("cdm.site_control", v)} />
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] pt-1">Critical actions</div>
                <TaggedTable testId="wsr-cdm-critical-actions" addLabel="Add critical action"
                          columns={[{ key: "action", label: "Critical action", width: "44%", textarea: true }, { key: "status", label: "Status", width: "20%", type: "select", optionsFromRow: true }, { key: "owner_due", label: "Owner / due", width: "36%" }]}
                          rows={g("cdm.critical_actions", [])} disabled={!canEdit} onChange={(v) => set("cdm.critical_actions", v)} />
                <CommentField value={g("cdm.narrative")} disabled={!canEdit} onChange={(v) => set("cdm.narrative", v)} testId="wsr-cdm-narrative" />
            </SectionCard>

            {/* 9.0 Inductions & Toolbox */}
            <SectionCard n="9.0" title="Site Inductions & Toolbox Talks" reviewNote={isReturned ? sectionComments.inductions : null}>
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Toolbox talks — auto, from the talk register</div>
                <TaggedTable testId="wsr-toolbox-log" addLabel="Add toolbox talk" refBadge="Talk register"
                          columns={[{ key: "date", label: "Date", width: "14%" }, { key: "topic", label: "Topic", width: "30%" }, { key: "attendees_count", label: "# Attendees", width: "14%" }, { key: "attendees", label: "Attendees", width: "42%", textarea: true }]}
                          rows={g("inductions.toolbox_log", [])} disabled={!canEdit} onChange={(v) => set("inductions.toolbox_log", v)} />
                <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                    <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">{inductionCountSummary(g("inductions.induction_log", []))}</div>
                    <Link to={`/app/inductions?tab=records&project_id=${report.project_id}&date_from=${report.week_start}&date_to=${report.week_end}`}
                          target="_blank" data-testid="wsr-induction-register-link" className="text-[11.5px] font-semibold text-[#2A5DB0] hover:underline">
                        View in induction register
                    </Link>
                </div>
                <TaggedTable testId="wsr-induction-log" addLabel="Add induction" refBadge="Inductions"
                          columns={[{ key: "date", label: "Date", width: "14%" }, { key: "name", label: "Name", width: "28%" }, { key: "company", label: "Company", width: "28%" }, { key: "person_type", label: "Person type", width: "30%" }]}
                          rows={g("inductions.induction_log", []).map((r) => ({ ...r, person_type: r.person_type_display || r.person_type }))}
                          disabled={!canEdit} onChange={(v) => set("inductions.induction_log", v)} />
                <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                    <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Inductions checklist</div>
                    {canEdit && (
                        <button type="button" data-testid="wsr-inductions-mark-remaining-yes"
                                onClick={() => markRemainingYes([["inductions.checklist", INDUCTIONS_CHECKLIST_ITEMS]])}
                                className="text-[11.5px] font-semibold text-[#C75A1B] hover:underline">
                            Mark remaining as Yes
                        </button>
                    )}
                </div>
                <ChecklistGrid items={INDUCTIONS_CHECKLIST_ITEMS} value={g("inductions.checklist", {})} disabled={!canEdit} onChange={(v) => set("inductions.checklist", v)}
                               testId="wsr-inductions-checklist" showNeedsAnswer onRaiseAction={(item) => raiseAction("inductions", "checklist", item)} raisingItem={raisingItem} />
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] pt-1">Competence checks - subcontractors</div>
                <div className="grid sm:grid-cols-2 gap-3 pt-1">
                    <div className="space-y-1.5">
                        <Field label="Expired cards identified">
                            <CheckPillGroup value={g("inductions.competence.expired_cards")} disabled={!canEdit}
                                            onChange={(v) => setExpiredCards("expired_cards", v)} testId="wsr-competence-expired" />
                        </Field>
                        <TextInput value={g("inductions.competence.expired_cards_detail")} disabled={!canEdit} placeholder="Names & expiry dates"
                                   onChange={(v) => setExpiredCards("expired_cards_detail", v)} testId="wsr-competence-expired-detail" />
                    </div>
                    <div className="space-y-1.5">
                        <Field label="Repeat offenders">
                            <CheckPillGroup value={g("inductions.competence.repeat_offenders")} disabled={!canEdit}
                                            onChange={(v) => set("inductions.competence.repeat_offenders", v)} testId="wsr-competence-repeat" />
                        </Field>
                        <TextInput value={g("inductions.competence.repeat_offenders_detail")} disabled={!canEdit} placeholder="Details"
                                   onChange={(v) => set("inductions.competence.repeat_offenders_detail", v)} testId="wsr-competence-repeat-detail" />
                    </div>
                </div>
                <CommentField value={g("inductions.narrative")} disabled={!canEdit} onChange={(v) => set("inductions.narrative", v)} testId="wsr-inductions-narrative" />
            </SectionCard>

            {/* 10.0 Risks */}
            <SectionCard n="10.0" title="Risks & Issues" reviewNote={isReturned ? sectionComments.risks : null}>
                <RisksTable testId="wsr-risks" role={user?.role} canDecide={canReview} onDecide={decideRisk}
                          rows={g("risks.items", [])} disabled={!canEdit} onChange={(v) => set("risks.items", v)}
                          onDismiss={dismissRisk} />
                <CommentField value={g("risks.narrative")} disabled={!canEdit} onChange={(v) => set("risks.narrative", v)} testId="wsr-risks-narrative" />
            </SectionCard>

            {/* 11.0 Client Actions */}
            <SectionCard n="11.0" title="Client Actions & Instructions Required" reviewNote={isReturned ? sectionComments.client_actions : null}>
                <ClientActionsTable testId="wsr-client-actions" rows={g("client_actions.items", [])} disabled={!canEdit}
                                    onChange={(v) => set("client_actions.items", v)}
                                    narrative={g("client_actions.narrative")} onNarrativeChange={(v) => set("client_actions.narrative", v)} />
            </SectionCard>

            {/* 12.0 Evidence */}
            <SectionCard n="12.0" title="Supporting Information" reviewNote={isReturned ? sectionComments.evidence : null}>
                <OtherDevicePendingBanner pendingUploads={report?.pending_uploads} canClear={canEdit}
                    onClear={async (devId) => { await clearOtherDevicePending("weekly_report", id, devId); await reloadReport(); }} />
                <PhotoGrid photos={g("evidence.photos", [])} apiBase={API} reportId={id} disabled={!canEdit}
                           pendingPhotos={pendingPhotos} onRetryPending={retryQueuedPhoto} onDiscardPending={discardQueuedPhoto} onDiscardAllPending={discardAllPendingPhotos}
                           onUpload={uploadPhoto} onDelete={deletePhoto} onCaption={captionPhoto} />
                {canEdit && (
                    <button type="button" data-testid="wsr-open-photo-picker" onClick={() => setPhotoPickerOpen(true)}
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[#C75A1B] hover:underline">
                        <Camera size={13} /> Add from this week's photos
                    </button>
                )}
                <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
                    <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Attachments / linked records</div>
                    {canEdit && (
                        <div className="flex items-center gap-3">
                            <label className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold ${uploadingAttachment ? "text-[#8B867E]" : "text-[#C75A1B] cursor-pointer hover:underline"}`}>
                                {uploadingAttachment ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}
                                {uploadingAttachment ? "Uploading…" : "Upload document"}
                                <input data-testid="wsr-attachment-upload-input" type="file" className="hidden" disabled={uploadingAttachment}
                                       onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ""; }} />
                            </label>
                            <button type="button" data-testid="wsr-open-link-record" onClick={() => setLinkRecordOpen(true)}
                                    className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[#C75A1B] hover:underline">
                                <Link2 size={13} /> Link a record
                            </button>
                        </div>
                    )}
                </div>
                <TaggedTable testId="wsr-attachments" addLabel="Add attachment / record"
                          columns={[{ key: "title", label: "Attachment / record", width: "30%" }, { key: "description", label: "Description", width: "42%", textarea: true }, { key: "reference", label: "Reference / link", width: "28%", type: "link" }]}
                          rows={g("evidence.attachments", [])} disabled={!canEdit} onChange={(v) => set("evidence.attachments", v)} />
                <CommentField value={g("evidence.narrative")} disabled={!canEdit} onChange={(v) => set("evidence.narrative", v)} testId="wsr-evidence-narrative" />
                <PhotoSourcePickerDialog open={photoPickerOpen} onClose={() => setPhotoPickerOpen(false)} reportId={id} onAdd={addLinkedPhoto} />
                <LinkRecordDialog open={linkRecordOpen} onClose={() => setLinkRecordOpen(false)} projectId={report.project_id}
                                  weekStart={report.week_start} weekEnd={report.week_end} onAdd={addLinkedAttachment} />
            </SectionCard>

            {/* 13.0 Sign-off */}
            <SectionCard n="13.0" title="Sign-Off">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Author"><TextInput value={g("sign_off.author_name")} disabled={!canEdit} onChange={(v) => set("sign_off.author_name", v)} testId="wsr-signoff-author" /></Field>
                    <Field label="Position"><TextInput value={g("sign_off.position")} disabled={!canEdit} onChange={(v) => set("sign_off.position", v)} testId="wsr-signoff-position" /></Field>
                    <Field label="Date"><TextInput type="date" value={g("sign_off.date")} disabled={!canEdit} onChange={(v) => set("sign_off.date", v)} testId="wsr-signoff-date" /></Field>
                    <Field label="Project / Contract"><TextInput value={`${report.project_name || ""} · ${g("header.contract_ref") || report.report_number}`} disabled onChange={() => {}} testId="wsr-signoff-project" /></Field>
                </div>
                <label className="flex items-start gap-2.5 text-[13px] text-[#1F1F1F] rounded-xl border border-[#E5E0DA] p-3 cursor-pointer">
                    <input type="checkbox" data-testid="wsr-signoff-declaration" className="mt-0.5 w-4 h-4 accent-[#C75A1B]"
                           checked={!!g("sign_off.declaration_confirmed")} disabled={!canEdit}
                           onChange={(e) => set("sign_off.declaration_confirmed", e.target.checked)} />
                    <span>Site Manager Declaration — {SIGN_OFF_DECLARATION}</span>
                </label>
                <Field label="Signature">
                    <SignaturePad value={g("sign_off.signature")} disabled={!canEdit} onChange={setSignature} />
                </Field>
                {report.status === "submitted" && (
                    <div className="text-[12.5px] text-[#6F6A64]">Submitted by {report.submitted_by_name} on {fmtDate(report.submitted_at)}</div>
                )}

                {senior?.status && (
                    <div data-testid="wsr-senior-review-block" className="rounded-xl border border-[#E5E0DA] p-3.5 space-y-1.5">
                        <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Senior Management Review</div>
                        <div className="text-[13px] font-bold" style={{ color: senior.status === "returned" ? "#B3261E" : senior.status === "accepted" ? "#2E7D32" : "#2A5DB0" }}>
                            {SENIOR_STATUS_LABEL[senior.status] || senior.status}
                        </div>
                        <p className="text-[12.5px] text-[#6F6A64]">by {senior.reviewer_name}{senior.reviewer_title ? ` · ${senior.reviewer_title}` : ""} on {fmtDateTime(senior.at)}</p>
                        {senior.comments && <p className="text-[12.5px] text-[#1F1F1F]">{senior.comments}</p>}
                        {typeof senior.signature === "string" && senior.signature.startsWith("data:image/") && (
                            <img src={senior.signature} alt="Reviewer signature" className="h-12 mt-1" />
                        )}
                        {typeof senior.signature === "string" && senior.signature && !senior.signature.startsWith("data:image/") && (
                            <p className="text-[13px] italic text-[#1F1F1F]">{senior.signature}</p>
                        )}
                    </div>
                )}

                {(report.revision_history || []).length > 0 && (
                    <div className="space-y-1.5">
                        <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E]">Revision history</div>
                        <div className="rounded-xl border border-[#E5E0DA] divide-y divide-[#F0ECE5] bg-white">
                            {report.revision_history.map((h, i) => (
                                <div key={i} data-testid={`wsr-revision-${i}`} className="px-3 py-2 text-[12.5px] flex flex-wrap gap-2 justify-between">
                                    <span className="font-semibold text-[#1F1F1F] capitalize">{h.status}</span>
                                    <span className="text-[#6F6A64]">{h.by_name} ({h.by_role}) · {fmtDateTime(h.at)}</span>
                                    {h.note && <span className="text-[#8B867E] w-full">{h.note}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </SectionCard>

            {canEdit && (
                <div className="flex justify-end gap-2 pb-8">
                    <button data-testid="wsr-submit-bottom" onClick={submit} disabled={submitting}
                            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212] disabled:opacity-50">
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit report
                    </button>
                </div>
            )}
            </>
            )}
        </div>
    );
}

// Prompt 13, item C — Export menu (PDF / Word), available on drafts and submitted reports.
function ExportMenu({ onPdf, onDocx }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button data-testid="wsr-export-menu-trigger" onClick={() => setOpen((o) => !o)}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]">
                <Download size={13} /> Export <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full mt-1.5 z-20 bg-white rounded-xl border border-[#E5E0DA] shadow-lg py-1.5 w-40">
                        <button data-testid="wsr-export-pdf" onClick={() => { setOpen(false); onPdf(); }}
                                className="w-full text-left px-3 py-2 text-[12.5px] font-semibold text-[#1F1F1F] hover:bg-[#F5F3F0]">PDF</button>
                        <button data-testid="wsr-export-docx" onClick={() => { setOpen(false); onDocx(); }}
                                className="w-full text-left px-3 py-2 text-[12.5px] font-semibold text-[#1F1F1F] hover:bg-[#F5F3F0]">Word (.docx)</button>
                    </div>
                </>
            )}
        </div>
    );
}

